import React, { useRef, useState } from 'react';
import * as Yup from 'yup';
import { ScopeCertificateDetails, StandardDetails } from '../../../sc-models/scope-certificate-details-dto';
import FormBuilder from '../../../../../shared/components/form-builder/form-builder';
import { OptionType } from '../../../../../shared/components/form-builder/form-config.model';
import { useScStore } from '../../../../../store/scStandard.store';
import { useParams } from 'react-router-dom';
import { notify } from '../../../../../shared/utils/notify.utills';
import { RestrictedDataValidation } from '../../../../../shared/utils/custom-validation.utils';

interface ScopeCertificateDetailsFormProps {
  formRef: any;
  detailsData: ScopeCertificateDetails;
  setDetailsData: React.Dispatch<React.SetStateAction<ScopeCertificateDetails>>;
  // standardData: StandardDetails[];
  // setStandardsData: React.Dispatch<React.SetStateAction<StandardDetails[]>>;
  SC_ID?: string;
}

const ScopeCertificateDetailsForm: React.FC<ScopeCertificateDetailsFormProps> = ({
  formRef,
  detailsData,
  setDetailsData,
  SC_ID
}) => {
  const { editSCInfo } = useScStore();
  const { formtype } = useParams();
  const hasSubmitSC: boolean = formtype === 'submited-sc';
  const statusOptions: OptionType[] = [
    { label: "Valid (V)", value: "V" }, // Represents "Valid"
    { label: "Expired (E)", value: "E" }, // Represents "Expired"
    { label: "Suspended (S)", value: "S" }, // Represents "Suspended"
    { label: "Withdrawn (W)", value: "W" }, // Represents "Withdrawn"
  ];
  const certificationStatuses: OptionType[] = [
    { label: "Critical non-conformity (C)", value: "C" }, // Represents "Critical non-conformity"
    { label: "Non-conformity (N)", value: "N" }, // Represents "Non-conformity"
    { label: "Recertification completed (R)", value: "R" }, // Represents "Recertification completed"
    { label: "Withdrawal of the certification body’s accreditation (W)", value: "W" }, // Represents "Withdrawal of accreditation"
    { label: "Administrative (A)", value: "A" }, // Represents "Administrative"
    { label: "Voluntary (V)", value: "V" }, // Represents "Voluntary"
    { label: "Other (O)", value: "O" }, // Represents "Other"
  ];
  const conformityStatuses: OptionType[] = [
    { label: "Critical non-conformity (C)", value: "C" }, // Represents "Critical non-conformity"
    { label: "Non-conformity (N)", value: "N" }, // Represents "Non-conformity"
    { label: "Administrative (A)", value: "A" }, // Represents "Administrative"
    { label: "Other (O)", value: "O" }, // Represents "Other"
  ];
  const formConfig = {
    pageName:"scope-certificate-details",
    initialValues: detailsData,
    validationSchema: Yup.object().shape({
      scopeCertificateNo: Yup.string()
        .required('Scope Certificate No. is required'),
      brandName: Yup.array()
        .of(Yup.string()),
      gotsSCNo: Yup.string(),
      scLegacyNo: Yup.array()
      .of(Yup.string()),
      scCBAuditorId: Yup.array()
      .of(Yup.string()),
      scStandardProgram: Yup.string(),
      scStatus: Yup.string().required("SC Status is required"),
      scWithdrawalReason: Yup.string().when('scStatus', {
        is: 'W',
        then: (schema) => schema.required('Withdrawal Reason is required'),
        otherwise: (schema) => schema.optional()
      }),
      scSuspensionReason: Yup.string().when('scStatus', {
        is: 'S',
        then: (schema) => schema.required('Suspension Reason is required'),
        otherwise: (schema) => schema.optional()
      }),
       scVersionNo: Yup.string()
        .required('SC Version No. is required')
        .min(6, 'Minimum 6 characters are required'),
        ccsFlag: Yup.string()
         .required('Choose whether CCS Applies'),
      // globalComment: Yup.string(),
    }),
    readOnly: hasSubmitSC,
    sections: [
      {
        title: 'Scope Certificate Details',
        fields: [
          {
            label: 'Scope Certificate No.',
            name: 'scopeCertificateNo',
            type: 'text',
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            disabled: (editSCInfo?.scId === SC_ID && editSCInfo?.isParentStatus) ? true : false,
            props: {placeholder: 'Enter Scope Certificate No.' },
            events: {
              onChange: (updatedValue: any, formValues: any, setFieldValue: (arg0: string, arg1: any) => void) => {
                setFieldValue("scVersionNo", updatedValue);
              },
            },
          },
          {
            label: 'SC Version No.',
            name: 'scVersionNo',
            type: 'text',
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            disabled:  false,
            props: {placeholder: 'Enter SC Version No.' },
          },
          
          {
            label: 'GOTS SC No.',
            name: 'gotsSCNo',
            type: 'text',
            gridSize: { xs: 12, md: 6, lg: 4 },
            required:false,
            props: { placeholder: 'Enter GOTS SC No. (required if applicable)' },
          },
          {
            label: 'Brand Name',
            name: 'brandName',
            type: 'chips',
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: false,
            props: {
              placeholder: 'Enter Brand Name(s)',
            },
          },
          {
            label: 'SC Legacy No.',
            name: 'scLegacyNo',
            type: 'chips',
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: false,
            props: { placeholder: 'Enter SC Legacy No. (optional)' },
          },
          {
            label: 'SC CB Auditor ID',
            name: 'scCBAuditorId',
            type: 'chips',
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: false,
            props: {
              placeholder: 'Enter CB Auditor ID',
            },
          },
          {
            label: 'SC Standard Program',
            name: 'scStandardProgram',
            type: 'select',
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: false,
            props: {
              options: [
                { label: "None", value: "" },
                { label: "Responsible Animal Fiber", value: "RAS,RWS,RAS" },
                { label: "Content Claim Standard", value: "CCS" },
              ],
              placeholder: 'Select Standard Program',
            },
          },
          {
            label: 'SC Status',
            name: 'scStatus',
            type: 'select',
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            props: {
              options: statusOptions,
              placeholder: 'Select SC Status',
            },
            events: {
              onChange: (value: any, formValues: any, setFieldValue: (field: string, value: any) => void) => {
                // Reset withdrawal reason if status is not 'W'
                if (value !== 'W') {
                  setFieldValue('scWithdrawalReason', '');
                }
                // Reset suspension reason if status is not 'S'
                if (value !== 'S') {
                  setFieldValue('scSuspensionReason', '');
                }
              },
            },
          },
          {
            label: 'SC Withdrawal Reason',
            name: 'scWithdrawalReason',
            type: 'select',
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: (values: any) => values.scStatus === "W",
            disabled: (values: any) => values.scStatus !== "W",
            visible: (values: any) => values.scStatus === "W",
            props: {
              options: certificationStatuses,
              placeholder: 'Select Withdrawal Reason (if applicable)',
            },
          },
          {
            label: 'SC Suspension Reason',
            name: 'scSuspensionReason',
            type: 'select',
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: (values: any) => values.scStatus === "S",
            disabled: (values: any) => values.scStatus !== "S",
            visible: (values: any) => values.scStatus === "S",
            props: {
              options: conformityStatuses,
              placeholder: 'Select Suspension Reason (if applicable)',
            },
          }, 
          {
            label: 'Does CCS Apply',
            name: 'ccsFlag',
            type: 'radio',
            gridSize: { xs: 12, md: 4, lg: 4 }, // Changed to full width
            required: true,
            // value: initialValues.certifiedOrganizationPublicEmail,
            props: {
              options: [
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" },
              ],
            },
          },         
          // {
          //   label: 'Global Comment Not For Actual Data To Be Provided',
          //   name: 'globalComment',
          //   type: 'text',
          //   gridSize: { xs: 12, md: 6, lg: 4 },
          //   required: false,
          //   props: { placeholder: 'Enter Global Comment' },
          // },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      setDetailsData(values);
    },
  };

  return <FormBuilder formConfig={formConfig} onSubmit={formConfig.handleSubmit} formRef={formRef} />;
};

export default ScopeCertificateDetailsForm;

